import { DynamicPageProps } from '@doltech/routeDefinition';
import { FooterBanner } from '@doltech/ui/lib/banner/component/HeaderBanner';
import { dolDinhLucAuthor } from '@doltech/ui/lib/figma/Blog/dolDinhLucAuthor.data';
import { Box } from '@doltech/ui/lib/figma/Common/BoxV2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { DownloadPDF } from '@doltech/ui/lib/figma/Layout/Landing/Content/DownloadPdf';
import { PdfPage } from '@doltech/ui/lib/figma/Layout/Landing/Content/PageBreakPdf';
import {
  SectionOfContent,
  SectionOfContentPdf,
} from '@doltech/ui/lib/figma/Layout/Landing/Content/SectionOfContent';
import { Separator } from '@doltech/ui/lib/figma/Layout/Landing/Content/Separator';
import { AuthorInfo } from '@doltech/ui/lib/figma/Layout/Landing/Header/AuthorInfo';
import { useLandingSharedDataStore } from '@doltech/ui/lib/figma/Layout/Landing/model/landing-shared-data.hook';
import { LandingLayoutWithSEOHeader } from '@doltech/ui/lib/figma/Layout/Landing/PremadeLayout/LandingLayoutWithSEOHeader';
import { deviceBreakpoints } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { ExtraInfoView } from '@doltech/ui/lib/shared/components/ExtraInfo/ExtraInfoView';
import { SkillEnum } from '@doltech/utils/lib/constants';
import { PDFSourceContext, usePdfTrackingParams } from '@doltech/utils/lib/pdf';
import { getLandingUrlFromPath, mergeUrlWithParams } from '@doltech/utils/lib/url';
import { take } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { bannerHomePage, bannerOnlineTest } from '../admin-blog-management/hook/bannerImages';
import { useViewTestSubjectStore } from '../view-test-subject/model/view-test-subject.hook';
import { PromoteIELTSVocabLandingSection } from '../view-test-subject/PromotionPdf';
import { ViewSubjectHeaderWithTestAction } from '../view-test-subject/ViewSubjectHeader';
import { VocabExamplePDF } from './components/VocabExamplePDF';
import { VocabList, VocabListProps } from './components/VocabList';

interface ViewVocabLayoutProps extends DynamicPageProps {}

const Main = styled.div.withConfig({
  componentId: 'ViewVocabLayout_Main',
} as any)`
  .hidden {
    display: none;
  }
  .section-content {
    padding-top: 24px;
  }

  .vocab-list-container {
    .soc-title {
      padding: 0;
    }
    .soc-box {
      background-color: transparent;
      box-shadow: none;
    }
  }

  .vocab-list {
    margin: 20px 0;
    @media ${deviceBreakpoints.mobile} {
      flex-direction: column;
    }
    .column {
      flex: 1;

      &:not(:last-child) {
        @media ${deviceBreakpoints.desktop} {
          margin-right: 16px;
        }

        @media ${deviceBreakpoints.tablet} {
          margin-right: 24px;
        }
      }
    }

    .vocab-item {
      @media ${deviceBreakpoints.allExceptMobile} {
        &:first-child {
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
        }

        &:last-child {
          border-bottom-left-radius: 16px;
          border-bottom-right-radius: 16px;
        }
      }

      &:not(:last-child) {
        margin-bottom: 8px;
      }
    }
  }
`;

export const VocabListPDF = ({ vocabList, pageUrl }: VocabListProps) => {
  return (
    <>
      {vocabList.map((vocab) => {
        return <VocabExamplePDF key={`${vocab.pronounce}-pdf`} {...vocab} pageUrl={pageUrl} />;
      })}
    </>
  );
};

interface SectionVocabListProps {
  headerText: string;
  sectionIndex: number;
  children: JSX.Element;
}

export const SectionVocabList = observer(({ children, sectionIndex }: SectionVocabListProps) => {
  const store = useViewTestSubjectStore();

  return (
    <SectionOfContent
      className="vocab-list-container"
      id={`test-section-${sectionIndex}`}
      sectionId={`test-section-${sectionIndex}`}
      tableOfContent={store.getVocabTableOfContent()}
      renderTitle={(title) => (
        <Typography.Title className="font-inter" variant="medium/24" level={2}>
          {title}
        </Typography.Title>
      )}
    >
      {children}
    </SectionOfContent>
  );
});

const SectionVocabListPdf = observer(({ children, sectionIndex }: SectionVocabListProps) => {
  const store = useViewTestSubjectStore();

  return (
    <SectionOfContentPdf
      sectionId={`test-section-${sectionIndex}`}
      tableOfContent={store.getVocabTableOfContent()}
    >
      {children}
    </SectionOfContentPdf>
  );
});

const CONTENT_PDF_WIDTH = 848;
const VocabListOfSectionsPdfWrapper = styled.div`
  width: ${CONTENT_PDF_WIDTH}px;
  padding: 0 22px;
`;
const VocabListOfSectionsPdf = ({ pageUrl }) => {
  const store = useViewTestSubjectStore();
  const pdfTrackingParams = usePdfTrackingParams();
  return (
    <VocabListOfSectionsPdfWrapper>
      {store.testSections.map((testSection, testSectionIndex) => {
        let vocabList = testSection.vocab?.vocabs || [];

        if (store.testSections.length > 1) {
          vocabList = take(vocabList, 8);
        }
        let Wrapper = React.Fragment as any;
        if (testSectionIndex < store.testSections.length - 1) {
          Wrapper = PdfPage;
        }
        return (
          <Wrapper key={testSectionIndex}>
            <SectionVocabListPdf
              key={testSectionIndex}
              sectionIndex={testSectionIndex + 1}
              headerText={testSection.name}
            >
              <VocabListPDF vocabList={vocabList} pageUrl={pageUrl} />
            </SectionVocabListPdf>
            <PromoteIELTSVocabLandingSection
              title={testSection.name}
              url={mergeUrlWithParams(getLandingUrlFromPath(pageUrl), pdfTrackingParams)}
            />
          </Wrapper>
        );
      })}
    </VocabListOfSectionsPdfWrapper>
  );
};
export const ViewVocabLayout = observer((props: ViewVocabLayoutProps) => {
  const { internalPageLink, data, seoData, pageData } = props;
  const { extraInfo } = seoData;
  const store = useViewTestSubjectStore();
  const { getHomePageLink } = useLandingSharedDataStore();

  const { banner, bannerLink } = React.useMemo(() => {
    switch (data.skill) {
      case SkillEnum.READING:
      case SkillEnum.LISTENING: {
        return {
          banner: bannerOnlineTest,
          bannerLink: internalPageLink?.staticPageLink?.LANDING_FULL_TEST?.url || '/',
        };
      }
      default:
    }
    return {
      banner: bannerHomePage,
      bannerLink: internalPageLink?.staticPageLink?.LANDING_HOME?.url || '/',
    };
  }, [
    data.skill,
    internalPageLink?.staticPageLink?.LANDING_FULL_TEST?.url,
    internalPageLink?.staticPageLink?.LANDING_HOME?.url,
  ]);

  if (!store.initialed) return null;

  return (
    <LandingLayoutWithSEOHeader
      testType={data.testType}
      testSkill={store.skill}
      tableOfContentItems={store.getVocabTableOfContent()}
      testId={data.id}
      data={data}
      seoData={seoData}
      pageData={pageData}
      socials={[]}
      banner={banner}
      bannerLink={bannerLink}
    >
      <Main className="practice-test">
        {store.testSections.map((testSection, testSectionIndex) => {
          const vocabList = testSection.vocab?.vocabs || [];
          return (
            <React.Fragment key={testSectionIndex}>
              <SectionVocabList
                key={testSectionIndex}
                sectionIndex={testSectionIndex + 1}
                headerText={testSection.name}
              >
                <VocabList vocabList={vocabList} />
              </SectionVocabList>
              {testSectionIndex + 1 < store.testSections.length && (
                <Separator
                  key={`${testSectionIndex}-divider`}
                  pt="0"
                  pb="40px"
                  variant="three-dot-horizontal"
                />
              )}
            </React.Fragment>
          );
        })}
        <PDFSourceContext.Provider value={{ source: 'test' }}>
          <DownloadPDF
            id="test-section-download-pdf"
            testName={store.name || '...'}
            fileName={`${(store?.name || '').split(' ').join('_')}.pdf`}
            homepageUrl={getLandingUrlFromPath(getHomePageLink())}
            isVocab
          >
            <ViewSubjectHeaderWithTestAction
              seoData={seoData}
              seoPageData={pageData?.pageDetail?.pages}
            />
            <VocabListOfSectionsPdf pageUrl={seoData.url} />
          </DownloadPDF>
        </PDFSourceContext.Provider>
        {Boolean(extraInfo) && (
          <>
            <Separator pt="40px" pb="24px" variant="three-dot-horizontal" />
            <Box mb="24px">
              <ExtraInfoView extraInfo={extraInfo} fontInter />
            </Box>
          </>
        )}
        <AuthorInfo
          pageId={seoData.id}
          id="footer-author-info"
          author={dolDinhLucAuthor}
          publishedDate={data?.lastModifiedAt ? data.lastModifiedAt : data.createdAt}
          seoUrl={seoData.url}
          withBorder
          withActions
        />
        <Box mb={32}>
          <FooterBanner />
        </Box>
      </Main>
    </LandingLayoutWithSEOHeader>
  );
});
